import {
  CertificationSchemeToLabel,
  conversionProcessToLabel,
} from '../calculations/constants.js';
import {
  claimMetaComplianceRegimeToString,
  type ClaimMetadata,
} from '../claim-meta.js';
import {
  AdditionalityApproachType,
  AdditionalityApproachTypeToLabel,
  type BatchAdditionalityApproach,
} from '../unit-meta/batch.js';
import type {
  CertificationScheme,
  CertificationSchemeOther,
} from '../unit-meta/certification.js';
import { issueFields } from '../unit-meta/issueFormFields.js';
import type { RegulatoryScheme } from '../unit-meta/shared.js';
import {
  type ConversionProcess,
  type Country,
  RegulatorySchemeToLabel,
} from '../unit-meta/shared.js';

const ensureArray = <T>(value: T | T[]): T[] =>
  (Array.isArray(value) ? value : [value]);

type Field = {
  key: string;
  label: string;
  format?: 'date' | 'unitId';
  tooltip?: string;
  helpText?: string;
  map?: (value: any) => string;
};

export const definitions = {
  displayFields: {
    title: 'Issuance data',
    categories: [
      {
        name: 'Batch',
        fields: [
          {
            label: 'Fuel producing company name',
            key: 'computed.rootMetadata.batch.companyName',
          },
          {
            label: 'Fuel provider company name',
            key: 'computed.rootMetadata.batch.fuelProviderCompanyName',
          },
          { // show this for quick access on first tab
            label: 'Mass of neat SAF',
            key: 'computed.rootMetadata.product.quantityMT',
          },
          { // show this for quick access on first tab
            label: 'Fuel producing facility name',
            key: 'computed.rootMetadata.supplier.siteName',
          },
          { // show this for quick access on first tab
            label: 'Fuel producing address',
            key: 'computed.rootMetadata.supplier.siteAddress',
          },
          {
            label: 'Date of SAF leaving the issuer\'s chain of custody',
            key: 'computed.rootMetadata.batch.leaveDate',
          },
          {
            label: 'Batch Number of SAF',
            key: 'computed.rootMetadata.batch.batchNumber',
          },
          {
            label: 'Year of SAF production',
            key: 'computed.rootMetadata.batch.productionYear',
          },
          {
            label: 'Country of SAF uplift',
            key: 'computed.rootMetadata.batch.upliftCountry.name',
          },
          {
            label: 'Airport of SAF uplift',
            key: 'computed.rootMetadata.batch.upliftAirport',
          },
          {
            label: 'Regulatory schemes applicable to the production of the SAF',
            key: 'computed.rootMetadata.batch.regulatorySchemesDeclaration',
            map: (schemes: RegulatoryScheme[]) => schemes.map(scheme => RegulatorySchemeToLabel[scheme]).join(', '),
          },
          {
            label: issueFields['batch.additionalityApproach.type'].title,
            key: 'computed.rootMetadata.batch.additionalityApproach.type',
            map: (value: AdditionalityApproachType) => AdditionalityApproachTypeToLabel[value],
          },
          {
            label: issueFields['batch.additionalityApproach.approachName'].title,
            key: 'computed.rootMetadata.batch.additionalityApproach.approachName',
          },
          {
            label: issueFields['batch.additionalityApproach.allowScope3Claims'].title,
            key: 'computed.rootMetadata.batch.additionalityApproach.allowScope3Claims',
          },
        ],
      },
      {
        name: 'Blending',
        fields: [
          {
            label:
              'Date of SAF blending with conventional jet fuel or certification as drop-in?',
            key: 'computed.rootMetadata.blending.blendingDate',
          },
          {
            label:
              'Location of SAF blending with conventional jet fuel or certification as drop-in',
            key: 'computed.rootMetadata.blending.blendingLocation',
          },
        ],
      },
      {
        name: 'Supplier',
        fields: [
          {
            label: 'Fuel producing facility name',
            key: 'computed.rootMetadata.supplier.siteName',
          },
          {
            label: 'Fuel producing address',
            key: 'computed.rootMetadata.supplier.siteAddress',
          },
        ],
      },
      {
        name: 'Certification',
        fields: [
          {
            label: 'Fuel certification scheme',
            key: 'computed.certificationScheme',
          },
          {
            label: 'Actual or Default LCA',
            key: 'computed.rootMetadata.certification.type',
          },
          {
            label: issueFields['certification.hasIRS7210ForIsccPlus'].title,
            key: 'computed.rootMetadata.certification.hasIRS7210ForIsccPlus',
          },
          {
            label:
              'Certification number of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.certificateNumber',
          },
          {
            label:
              'RSB or ISCC Trader Certification number of the company that created the POS',
            key: 'computed.rootMetadata.certification.traderCertificationNumber',
          },
          {
            label:
              'Name of the certification body responsible for the certification of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.certificationBody',
          },
          {
            label:
              'Expiry date of the certification of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.expiryDate',
          },
          {
            label:
              'Additional fuel certification schemes standards and evaluation methodologies',
            key: 'computed.rootMetadata.certification.additionalScheme',
          },
          {
            label: issueFields['certification.additionalHasIRS7210ForIsccPlus'].title,
            key: 'computed.rootMetadata.certification.additionalHasIRS7210ForIsccPlus',
          },
          {
            label:
              'Additional certification number of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.additionalCertificateNumber',
          },
          {
            label:
              'Expiry date of the additional certification of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.additionalExpiryDate',
          },
          {
            label:
              'Name of the additional certification body responsible for the certification of the company that has produced the SAF',
            key: 'computed.rootMetadata.certification.additionalCertificationBody',
          },
          {
            label:
              'Does your certification include RSB Low iLUC Risk Biomass Criteria and Compliance Indicators (RSB-STD-04-001) or ISCC equivalent?',
            key: 'computed.rootMetadata.certification.isLowILUCRiskConfirmed',
          },
          {
            label: issueFields['certification.lowRiskFollowUnusedLandApproach'].title,
            key: 'computed.rootMetadata.certification.lowRiskFollowUnusedLandApproach',
          },
          {
            label:
              'Does your certification include RSB methodology for estimating displacement emissions (RSB-STD-04-002) or ISCC equivalent?',
            key: 'computed.rootMetadata.certification.isLowDisplacementRiskConfirmed',
          },
        ],
      },
      {
        name: 'Product',
        fields: [
          {
            label: 'Mass of neat SAF',
            key: 'computed.rootMetadata.product.quantityMT',
          },
          {
            label: 'Volume of neat fuel',
            key: 'computed.rootMetadata.product.volumeOfNeatFuel',
          },
          {
            label: 'Volume of neat fuel unit',
            key: 'computed.rootMetadata.product.volumeOfNeatFuelUnit',
          },
          {
            label: 'The feedstock used to make the SAF',
            key: 'computed.productionFeedstock',
          },
          {
            label:
              'Is the feedstock a waste, by-product or residue?',
            key: 'computed.rootMetadata.product.feedStock.wasCertifiedAsProductionResidue',
          },
          {
            label: issueFields['product.feedStock.isEnergyCrop'].title,
            key: 'computed.rootMetadata.product.feedStock.isEnergyCrop',
          },
          {
            label: issueFields['product.feedStock.isLanduseBasedCropBased'].title,
            key: 'computed.rootMetadata.product.feedStock.isLanduseBasedCropBased',
          },
          {
            label: issueFields['product.feedStock.isCorsiaDefaultIlucLcaLteZero'].title,
            key: 'computed.rootMetadata.product.feedStock.isCorsiaDefaultIlucLcaLteZero',
          },
          {
            label: issueFields['product.feedStock.isInEuRed2Annex9PartA'].title,
            key: 'computed.rootMetadata.product.feedStock.isInEuRed2Annex9PartA',
          },
          {
            label: 'Is feedstock municipal solid waste?',
            key: 'computed.rootMetadata.product.feedStock.isSolidWaste',
          },
          {
            label: 'Feedstock country or countries of origin',
            key: 'computed.rootMetadata.product.countries',
            map: (countries: Country | Country[]) =>
              ensureArray(countries).map(country => country.name ?? country).join(', '),
          },
          {
            label: 'SAF conversion process',
            key: 'computed.rootMetadata.product.conversionProcess',
            map: (value: ConversionProcess) => conversionProcessToLabel[value],
          },
          {
            label: 'SAF lower heating value (MJ/kg)',
            key: 'computed.rootMetadata.product.lowerHeatingValue',
          },
          {
            label:
              'Conventional jet fuel baseline carbon intensity (well to wake) (g CO2e/MJ)',
            key: 'computed.rootMetadata.product.jetFuelBaselineCarbonIntensity',
          },
        ],
      },
      {
        name: 'Greenhouse gas information',
        fields: [
          {
            label: 'Total SAF LCA value (gCO2e/MJ)',
            key: 'computed.totalLCAValue',
          },
          {
            label: 'Induced Land Use Change (ILUC) value (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.inducedLandUseChange',
          },
          {
            label:
              'Value of Recycling Emissions Credits (RECs) in the total SAF LCA value (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.recyclingEmissionsCredits',
          },
          {
            label: 'Value of Landfill Emissions Credits (LECs) in the total SAF LCA value (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.landfillEmissionsCredits',
          },
          {
            label: 'GHG Emissions from displacement (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.ghgDisplacementEmissions',
          },
          {
            label: 'di = GHG Emissions from displacement (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.displacementEmissions',
          },
          {
            label: 'Production at source (e.g. feedstock cultivation)',
            key: 'computed.rootMetadata.greenhouseGas.production',
          },
          {
            label:
              'Conditioning at source (e.g. feedstock harvesting, collection and recovery)',
            key: 'computed.rootMetadata.greenhouseGas.conditioning',
          },
          {
            label: 'Feedstock processing and extraction',
            key: 'computed.rootMetadata.greenhouseGas.processing',
          },
          {
            label:
              'Feedstock transportation to processing and fuel production faclities',
            key: 'computed.rootMetadata.greenhouseGas.transportation',
          },
          {
            label: 'Fuel transportation and distribution to the blend point',
            key: 'computed.rootMetadata.greenhouseGas.fuelTransportation',
          },
          {
            label: 'Feedstock-to-fuel conversion processes',
            key: 'computed.rootMetadata.greenhouseGas.conversionProcesses',
          },
          {
            label: 'Fuel combustion in an aircraft engine',
            key: 'computed.rootMetadata.greenhouseGas.combustion',
          },
          {
            label:
              'esca = GHG emissions savings from soil carbon accumulation via improved agricultural mgmt (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.savingsFromSoil',
          },
          {
            label: issueFields['greenhouseGas.emissionsSavingsFromCSS'].title,
            key: 'computed.rootMetadata.greenhouseGas.emissionsSavingsFromCSS',
          },
          {
            label:
              'eec = emissions from extraction or cultivation of raw materials (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.extractionEmissions',
          },
          {
            label:
              'el = annualised (over 20 yrs) GHG emissions from carbon stock change due to land use change (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.annualisedEmissions',
          },
          {
            label: 'ep = emissions from processing (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.processingEmissions',
          },
          {
            label: 'etd = emissions from transport and distribution (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.transportEmissions',
          },
          {
            label: 'eu = emissions from the fuel in use (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.inUseFuelEmissions',
          },
          {
            label:
              'eccs = GHG emissions savings from carbon capture and geological storage (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.savingsFromCarbonAndStorage',
          },
          {
            label:
              'eccr = GHG Emissions savings from carbon capture and replacement (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.savingsFromCarbonAndReplacement',
          },
          {
            label:
              'eee = GHG emissions savings from excess electricity from cogeneration (gCO2e/MJ)',
            key: 'computed.rootMetadata.greenhouseGas.savingsFromExcess',
          },
          {
            label: 'Does the total LCA value include emissions from delivery to airport?',
            key: 'computed.rootMetadata.greenhouseGas.totalLCAValueincludeEmissionsFromDeliveryToAirport',
          },
          {
            label: 'Do you want to use the default 0.9 gCO2e/MJ value for delivery to airport?',
            key: 'computed.rootMetadata.greenhouseGas.useDefaultValueForDeliveryToAirport',
          },
          {
            label: 'The actual GHG intensity value of airport delivery (if not included in total LCA)',
            key: 'computed.rootMetadata.greenhouseGas.actualGHGIntensityAirportDelivery',
          },
        ],
      },
    ] as { name: string; fields: Field[] }[],
  },
  detailsFields: {
    'Beneficiaries and Holdings History': [
      {
        label: 'Air Transport Provider Beneficiary',
        helpText:
          'The entity on whose behalf the SAFcA is retired, also known as Scope 1 claims beneficiary',
        key: 'computed.SAFcAClaimBeneficiaryCompanyName',
      },
      {
        label: 'Unique companies that have held the unit',
        key: 'computed.historyCompanyCount',
      },
      {
        label: 'End-User Beneficiary',
        helpText:
          'The end-user entity on whose behalf the SAFcE is retired, also known as Scope 3 claims beneficiary',
        key: 'computed.SAFcEClaimBeneficiaryCompanyName',
      },
      {
        label: 'Accounts that held unit',
        key: 'computed.historyOwnerTypes',
      },
      {
        label: 'Logistics Provider Beneficiary',
        helpText:
          'The logistics provider entity on whose behalf the SAFcE is retired, also known as Scope 3 claims beneficiary',
        key: 'computed.logisticsProviderBeneficiaryCompanyName',
      },
      {
        label: 'Issuing SAFc ID',
        key: 'computed.absoluteRootId',
        format: 'unitId',
        helpText: 'This is the unit ID of the SAFc that was originally issued',
      },
    ],
    'Dates': [
      {
        label: 'Year of SAF production',
        key: 'computed.rootMetadata.batch.productionYear',
      },
      {
        label: 'Issuance date',
        key: 'computed.issuanceDate',
        format: 'date',
        tooltip: 'computed.issuanceDate',
      },
      {
        label: 'Expiry date',
        key: 'computed.expirationDate',
        format: 'date',
        tooltip: 'computed.expirationDate',
      },
      {
        label: 'Retirement date',
        key: 'computed.retiredAt',
        format: 'date',
        tooltip: 'computed.retiredAt',
      },
      {
        label: 'Retirement claim year',
        key: 'computed.retirementYear',
      },
    ],
    'Sustainability': [
      {
        label: '% of emission reduction compared to baseline',
        key: 'parsed.ghgReduction',
      },
      {
        label: 'SAF carbon intensity (g CO2e/MJ)',
        helpText:
          'This value may have been adjusted after re-calculating submitted entries to determine the unit’s sustainability tier. For more on unit sustainability tiers, see <a href="https://energy-web-foundation.gitbook.io/safc-registry/user-guide/vi.-saf-certificates" target="_blank" rel="noopener noreferrer">see section V of the registry user guide</a>.',
        key: 'computed.carbonIntensityScore',
      },
      {
        label: 'Fuel Certification Scheme',
        key: 'computed.rootMetadata.certification.scheme',
        map: (scheme: CertificationScheme | CertificationSchemeOther) => {
          return CertificationSchemeToLabel[scheme];
        },
      },
      {
        label:
          'Conventional jet fuel baseline carbon intensity (well to wake) (g CO2e/MJ)',
        key: 'computed.rootMetadata.product.jetFuelBaselineCarbonIntensity',
        map: (value: string) => value.replace('g', ''),
      },
      {
        label: 'Regulatory schemes applicable to the production of the SAF',
        key: 'computed.rootMetadata.batch.regulatorySchemesDeclaration',
        map: (schemes: RegulatoryScheme[]) => schemes.map(scheme => RegulatorySchemeToLabel[scheme]).join(', '),
      },
      {
        label:
          'Additional fuel certification schemes standards and evaluation methodologies',
        key: 'computed.rootMetadata.certification.additionalScheme',
        map: (scheme: CertificationScheme | CertificationSchemeOther) => {
          return CertificationSchemeToLabel[scheme];
        },
      },
      {
        label: 'Government compliance regime',
        key: 'computed.complianceRegime',
        map: (regime: ClaimMetadata['complianceRegime']) => {
          return regime ? claimMetaComplianceRegimeToString(regime) : '';
        },
      },
      {
        label: 'Additionality approach',
        key: 'computed.rootMetadata.batch.additionalityApproach',
        map: (additionality: BatchAdditionalityApproach) => {
          if (additionality.type === AdditionalityApproachType.SABA) {
            return 'SABA additionality approach';
          } else {
            return additionality.approachName;
          }
        },
      },
    ],
    'Fuel production and use': [
      {
        label: 'Fuel producing company name',
        key: 'computed.rootMetadata.batch.companyName',
      },
      {
        label: 'Fuel provider company name',
        key: 'computed.rootMetadata.batch.fuelProviderCompanyName',
      },
      {
        label: 'Feedstock',
        key: 'computed.productionFeedstock',
      },
      {
        label: 'Feedstock country of origin',
        key: 'computed.rootMetadata.product.countries',
        map: (countries: Country | Country[]) =>
          ensureArray(countries).map(country => country.name ?? country).join(', '),
      },
      {
        label: 'Airport where SAF is delivered',
        key: 'computed.rootMetadata.batch.upliftAirport',
      },
      {
        label: 'Fuel producing facility name',
        key: 'computed.rootMetadata.supplier.siteName',
      },
      {
        label: 'SAF conversion process',
        key: 'computed.rootMetadata.product.conversionProcess',
        map: (value: ConversionProcess) => conversionProcessToLabel[value],
      },
      {
        label: 'Country of SAF Production',
        key: 'computed.countryOfProductionName',
      },
      {
        label: 'Emissions claim location',
        key: 'computed.claimPurpose',
        helpText: 'This is specified by the airline.',
      },

      {
        label: 'Country of SAF blending',
        key: 'computed.rootMetadata.blending.blendingLocation.country',
      },
    ],
  } satisfies Record<string, Field[]>,
  publicIssuanceTable: {
    fields: {
      blockId: {
        path: 'computed.blockId',
        label: 'Unit block ID',
      },
      productionYear: {
        path: 'computed.rootMetadata.batch.productionYear',
        label: 'Year of SAF production',
      },
      unitType: {
        path: 'computed.typeCode',
        label: 'Unit type',
      },
      unitCount: {
        path: 'volume',
        label: 'Neat SAF',
        description: 'Metric tonnes',
      },
      co2Abated: {
        path: 'computed.co2Abated',
        label: 'CO2e abated',
        description: 'Metric tonnes',
      },
      fuelProducingCompany: {
        path: 'computed.rootMetadata.batch.companyName',
        label: 'Fuel producing company',
      },
      carbonIntensityScore: {
        path: 'computed.carbonIntensityScore',
        label: 'SAF carbon intensity',
        description: 'gCO2e/MJ',
      },
      feedstockCountries: {
        path: 'computed.rootMetadata.blending.blendingLocation.country',
        label: 'Country of SAF blending',
      },
      certificationScheme: {
        path: 'computed.rootMetadata.certification.scheme',
        label: 'Fuel Certification Scheme',
        map: (scheme: CertificationScheme | CertificationSchemeOther) => {
          return CertificationSchemeToLabel[scheme];
        },
      },
      productionFeedstock: {
        path: 'computed.productionFeedstock',
        label: 'SAF feedstock',
      },
      createdAt: { // note: probably that name has to be change, but we are doing fix to production so I don't want to increase the scope
        path: 'computed.retiredAt',
        label: 'Retirement date',
        description: 'YYYY-MM-DD',
      },
      SAFcAClaimBeneficiaryCompanyName: {
        path: 'computed.SAFcAClaimBeneficiaryCompanyName',
        label: 'Air transport provider beneficiary',
        description: 'Scope 1',
      },
      SAFcEClaimBeneficiaryCompanyName: {
        path: 'computed.SAFcEClaimBeneficiaryCompanyName',
        label: 'End-user beneficiary',
        description: 'Scope 3',
      },
      logisticsProviderBeneficiaryCompanyName: {
        path: 'computed.logisticsProviderBeneficiaryCompanyName',
        label: 'Logistics provider beneficiary',
        description: 'Scope 3',
      },
    },
  },
} as const;
